/* Étape — RJ-02 (Bible §21/§35 · batch 10 · D-53).
   One demande and one roue. The demande comes from the library (Avis &
   réponses); the roue is the campaign’s by default — a roue of its own is the
   advanced option, and the odds of whichever is chosen are shown here so the
   choice is made on figures, not on a name. */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Card, ErrorState, KV, List, ListRow, Note, Pill } from '@/ui'
import { expectedCost, fmtEur, fmtPct, getRoues, odds, placesLabel } from '@/lib/wheel'
import { RIEN_NE_CONDITIONNE } from '@/lib/vocab'
import { useLocations } from '@/lib/api'
import { useStageDetail } from './api'
import { Chargement, DEMANDE_MOT, MARQUE, Retour } from './parts'

export default function Etape() {
  const { data, isPending, isError, refetch } = useStageDetail()
  const nav = useNavigate()
  const roues = getRoues()
  const { data: locations } = useLocations()
  const noms: Record<string, string> = Object.fromEntries((locations ?? []).map(l => [l.id, l.name]))
  const [choix, setChoix] = useState<string | null>(null)

  if (isPending) return <Chargement />
  if (isError) return (
    <ErrorState what="Impossible de charger l’étape."
      action={<Button variant="primary" size="sm" onClick={() => refetch()}>Réessayer</Button>} />
  )

  const laCampagne = roues[0]
  const choisie = roues.find(r => r.id === choix)
    ?? roues.find(r => r.name === data.roue)
    ?? laCampagne
  const propre = choisie && laCampagne ? choisie.id !== laCampagne.id : data.roueOwn
  const o = choisie ? odds(choisie) : {}

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 780 }}>
      <Retour />
      <h2 className="t-page" style={{ margin: 0 }}>{data.stage}</h2>
      <p className="f-help" style={{ margin: 0 }}>Une demande, une roue.</p>

      {/* ---------- the demande ---------- */}
      <section>
        <h3 className="t-eyebrow" style={{ margin: '0 0 8px' }}>Ce que vous demandez à cette visite</h3>
        <List>
          <ListRow icon={<span aria-hidden style={{ fontSize: 18 }}>{MARQUE[data.demandeKind]}</span>}
            title={<>{DEMANDE_MOT[data.demandeKind]} — {data.demande}</>}
            meta={data.demandeKind === 'avis'
              ? 'proposé seulement si cette plateforme n’a pas été ouverte à la visite précédente'
              : data.demandeKind === 'cadeau'
                ? 'aucune question — la visite est le cadeau'
                : 'composé dans Demandes ; changer la demande ici ne change rien au cadeau'}
            end={<Button size="sm" variant="quiet"
              onClick={() => nav(data.demandeKind === 'avis' ? '/destinations' : '/reponses')}>
              {data.demandeKind === 'avis' ? 'Liens d’avis' : 'Demandes'}
            </Button>} />
        </List>
      </section>

      {/* ---------- the roue ---------- */}
      <section>
        <h3 className="t-eyebrow" style={{ margin: '0 0 8px' }}>Roue de cette étape</h3>
        {roues.length === 0 ? (
          <Note tone="candour">Aucune roue pour l’instant — composez-en une, puis revenez choisir celle de cette étape.</Note>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {roues.map((r, i) => (
              <label key={r.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13 }}>
                <input type="radio" name="roue" className="chk" style={{ borderRadius: '50%', marginTop: 2 }}
                  checked={choisie?.id === r.id} onChange={() => setChoix(r.id)} />
                <span>
                  <b>{r.name}</b>{i === 0 && <> <Pill tone="ink">celle de la campagne</Pill></>}
                  <span className="f-help" style={{ display: 'block' }}>
                    {r.rewards.length} cadeaux · {fmtEur(expectedCost(r))} par participation · joue à {placesLabel(r, noms)}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}
      </section>

      {choisie && (
        <Card>
          <p className="t-eyebrow" style={{ marginTop: 0 }}>Chances de « {choisie.name} »</p>
          <div className="tablewrap">
            <table className="tbl">
              <thead><tr><th>Type de cadeau</th><th>Chance</th><th>Coût</th></tr></thead>
              <tbody>
                {choisie.rewards.map(x => (
                  <tr key={x.rewardId}>
                    <td>{x.name}{x.excluded && <span className="f-help"> · {x.excluded}</span>}</td>
                    <td>{fmtPct(o[x.rewardId] ?? 0)}</td>
                    <td>{x.cost ? fmtEur(x.cost) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Note tone="ok">
        {propre
          ? `Roue dédiée à cette étape : « ${choisie?.name} » au lieu de « ${laCampagne?.name} », la roue de la campagne.`
          : data.roueNote}
      </Note>

      <KV rows={[
        ['Repli si indisponible', data.fallback],
        ['Copie client', data.customerCopy],
      ]} />

      <Note tone="candour">{RIEN_NE_CONDITIONNE}</Note>
    </div>
  )
}
